import { useNavigate } from 'react-router-dom'
import { useAllPotentials, useOrderFeed, usePotentials } from '../basket/potentials'
import { Finder } from '../components/Finder'
import { useTranslation } from '../localization/useTranslation'
import { useCategoryOptions } from '../search/categories'
import { useAuthStore } from '../store/auth'
import { useSetPageTitle } from '../store/pageTitle'
import { Glyph, IconCheck, IconPlus } from '../ui/icons'
import { isDisplayableProductMetadata } from '../util/productMetadata'
import { useMoneyFmt } from '../util/useMoney'

/**
 * Items step. The shape adapts to the customer:
 *   - order-feed customer (has past online orders) → a selectable grid of those orders, plus an
 *     "Add manually" card that navigates to the finder page (`/add`) for anything bought elsewhere.
 *   - no-feed customer → the finder is the page; they start adding immediately.
 * Both feed a single rail on the right. Selecting an order or adding an item grows the basket.
 */
export function ItemsPage() {
  const { orders, hasFeed, isSelected, toggle, loading } = useOrderFeed()
  const { added } = useAllPotentials()
  const { potentials } = usePotentials()
  const config = useAuthStore((s) => s.config)
  const { t, locale } = useTranslation()
  const fmt = useMoneyFmt()
  const navigate = useNavigate()
  const defaultImage = config.defaultImage

  // Purchase date from the potential's `externDate` (the original order date), localised to
  // month + year — enough to convey how old the item is without false precision.
  const purchaseLabel = (iso?: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime())
      ? ''
      : new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(d)
  }

  const manualCount = added.length
  const has = potentials.length > 0
  // Whether the customer can add items that aren't in their own order feed (the "Find your item"
  // section, and the "Add manually" card for order-feed customers). Either mechanism qualifies:
  // product search (photo / catalogue) OR whole-sale categories that actually exist — `wholeSaleActive`
  // can be on with zero categories created, so we gate on the real category count, not the flag. A
  // shop with neither only accepts items from the customer's own past orders.
  const { hasCategories, loading: catsLoading } = useCategoryOptions()
  const canAddItems = Boolean(config.productSearchEnabled) || hasCategories
  // The category count may still be loading; don't commit to "nothing to add" until it's known.
  const addUnresolved = !config.productSearchEnabled && Boolean(config.wholeSaleActive) && catsLoading

  const [titleKey, subtitleKey] = hasFeed
    ? ['items.selectTitle', 'items.selectSubtitle']
    : has
      ? ['items.anotherTitle', 'items.anotherSubtitle']
      : canAddItems
        ? ['items.firstTitle', 'items.firstSubtitle']
        : ['items.nothingTitle', '']
  useSetPageTitle(t(titleKey), t(subtitleKey))

  // Initial load: don't flash the no-feed finder before the order feed resolves.
  if (loading && orders.length === 0 && added.length === 0) {
    return (
      <div className="center-note">
        <p>{t('auth.loadingItems')}</p>
      </div>
    )
  }

  // Order-feed customer — past-orders grid. "Add manually" navigates to the finder page (`/add`).
  if (hasFeed) {
    return (
      <div className="step">
        <div className="grid">
          {orders.map((o) => {
            const sel = isSelected(o._id)
            const image = o.imageUrls?.[0] || defaultImage
            const sub = isDisplayableProductMetadata(o.color)
              ? o.color
              : isDisplayableProductMetadata(o.size)
                ? o.size
                : ''
            const bought = purchaseLabel(o.externDate)
            return (
              <button
                key={o._id}
                type="button"
                className={`card ${sel ? 'sel' : ''}`}
                aria-pressed={sel}
                onClick={() => toggle(o._id)}
              >
                <div className="pic">
                  {image ? <img src={image} alt="" /> : <Glyph name="shirt" />}
                  <span className="check">
                    <IconCheck />
                  </span>
                </div>
                <div className="cbody">
                  <div className="nm">{o.name}</div>
                  {sub && <div className="sub">{sub}</div>}
                  {bought && <div className="bought">{t('items.bought', { date: bought })}</div>}
                  <div className="row">
                    <span className="eyebrow">{t('items.estReward')}</span>
                    <span className="amt num">{fmt(o.offer?.value)}</span>
                  </div>
                </div>
              </button>
            )
          })}

          {canAddItems && (
            <button className="card-add" type="button" onClick={() => navigate('/add')}>
              <IconPlus />
              <b>{t('items.addManually')}</b>
              <small>
                {manualCount > 0
                  ? t('items.addedCount', { count: manualCount })
                  : t('items.boughtElsewhere')}
              </small>
            </button>
          )}
        </div>
      </div>
    )
  }

  // No-feed customer who also can't add non-feed items (e.g. MOS MOSH, no past orders): there's
  // genuinely nothing for them to do here — say so plainly instead of showing an unusable finder.
  if (!canAddItems) {
    // Categories may still be resolving for a categories-only shop — wait rather than flash "nothing".
    if (addUnresolved) {
      return (
        <div className="center-note">
          <p>{t('auth.loadingItems')}</p>
        </div>
      )
    }
    return (
      <div className="step">
        <div className="items-empty">
          <span className="items-empty-ic">
            <Glyph name="shirt" />
          </span>
          <p>{t('items.nothingBody')}</p>
        </div>
      </div>
    )
  }

  // No-feed customer — the finder is the page.
  return (
    <div className="step">
      <Finder />
    </div>
  )
}
